import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { http, WS_BASE } from "@/lib/api";
import { useReconnectingSocket } from "@/hooks/useReconnectingSocket";
import { useBranding } from "@/context/BrandingContext";

const RealtimeCtx = createContext(null);

function beep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.06, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.35);
  } catch {
    /* audio indisponível */
  }
}

export function RealtimeProvider({ children }) {
  const listeners = useRef(new Set());
  const [newCount, setNewCount] = useState(0);
  const [sound, setSound] = useState(() => localStorage.getItem("rw_sound") === "1");
  const soundRef = useRef(sound);
  soundRef.current = sound;
  const { reload: reloadBranding } = useBranding();
  const brandingRef = useRef(reloadBranding);
  brandingRef.current = reloadBranding;

  useEffect(() => { localStorage.setItem("rw_sound", sound ? "1" : "0"); }, [sound]);

  const emit = (msg) => listeners.current.forEach((fn) => fn(msg));

  const onMessage = useCallback((msg) => {
    if (msg.event === "request.created") {
      setNewCount((c) => c + 1);
      toast(`Nova solicitação · CNPJ ${msg.data.cnpj_fmt}`, { description: `Protocolo ${msg.data.protocolo}` });
      if (soundRef.current) beep();
    }
    if (msg.event === "appointment.created") {
      setNewCount((c) => c + 1);
      toast.success(`Novo agendamento · ${msg.data.nome}`, { description: `Protocolo ${msg.data.protocolo}` });
      if (soundRef.current) beep();
    }
    if (msg.event === "redeem.requested") {
      setNewCount((c) => c + 1);
      toast.warning(`Resgate imediato solicitado · CNPJ ${msg.data.cnpj_fmt}`, { description: `Protocolo ${msg.data.protocolo}` });
      if (soundRef.current) beep();
      emit({ ...msg, event: "request.updated" });
    }
    if (msg.event === "action.completed") {
      toast.success(`Cliente respondeu "${msg.data.custom_action?.name}" · validar · ${msg.data.protocolo}`);
      if (soundRef.current) beep();
      emit({ ...msg, event: "request.updated" });
    }
    if (msg.event === "settings.updated") brandingRef.current?.();
    if (msg.event === "code.verified") {
      toast.success(`Cliente informou o código · validar · ${msg.data.protocolo}`);
      if (soundRef.current) beep();
      emit({ ...msg, event: "request.updated" });
    }
    emit(msg);
  }, []);

  const status = useReconnectingSocket({
    url: `${WS_BASE}/ws/admin`,
    onMessage,
    beforeConnect: () => http.post("/auth/refresh").catch(() => {}),
    onOpen: (wasReconnect) => wasReconnect && emit({ event: "resync" }),
  });

  const subscribe = useCallback((fn) => {
    listeners.current.add(fn);
    return () => listeners.current.delete(fn);
  }, []);

  const value = { status, subscribe, newCount, clearNew: () => setNewCount(0), sound, setSound };
  return <RealtimeCtx.Provider value={value}>{children}</RealtimeCtx.Provider>;
}

export const useRealtime = () => useContext(RealtimeCtx);

export function useRealtimeEvent(handler) {
  const { subscribe } = useRealtime();
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => subscribe((m) => ref.current(m)), [subscribe]);
}
